import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { translations } from '../components/LanguageSelector/translations';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  slug: string | null;
  isActive: boolean;
  translatedName?: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

const ProductsPage: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const t = translations[language] || translations.en;

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async (categorySlug?: string) => {
    try {
      setLoading(true);
      const url = categorySlug ? `/products/category/${categorySlug}` : '/products';
      const response = await api.get(url);
      setProducts(response.data);
    } catch (err) {
      setError(t.products.errorLoading || 'Error loading products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleCategoryFilter = (categorySlug: string | null) => {
    setSelectedCategory(categorySlug);
    fetchProducts(categorySlug || undefined);
  };



  const getCategoryLabel = (category: string | null) => {
    if (!category) return '';
    
    const categoryLabels: { [key: string]: string } = {
      'cheese': t.products.categories?.cheese || 'Cheese',
      'wine': t.products.categories?.wine || 'Wine',
      'olive-oil': t.products.categories?.oliveOil || 'Olive Oil',
      'pasta': t.products.categories?.pasta || 'Pasta',
      'salami': t.products.categories?.salami || 'Salami',
      'honey': t.products.categories?.honey || 'Honey',
      'truffle': t.products.categories?.truffle || 'Truffle'
    };

    return categoryLabels[category] || category;
  };

  const getProductPreviewImage = (product: Product) => {
    if (!product.category || !product.slug) return undefined;
    
    // Generate path for _1_small.jpg image
    const previewImagePath = `/products/${product.category.slug}/${product.slug}/${product.category.slug}_1_small.jpg`;
    
    // Return the preview image path, fallback to undefined if needed
    return previewImagePath;
  };

  const getProductDescription = (productName: string, category: string | null) => {
    // Map product names to translation keys
    const productMap: { [key: string]: string } = {
      'Montepulciano d\'Abruzzo DOC': 'products.wine.montepulciano.description',
      'Trebbiano d\'Abruzzo DOC': 'products.wine.trebbiano.description',
      'Pecorino Romano DOP': 'products.cheese.pecorino.description',
      'Caciocavallo Silano': 'products.cheese.caciocavallo.description',
      'Olio Extra Vergine di Oliva': 'products.oliveOil.extraVergine.description',
      'Pasta alla Chitarra': 'products.pasta.chitarra.description',
      'Lenticchie di Santo Stefano': 'products.pasta.lenticchie.description',
      'Salsiccia di Fegato': 'products.salami.salsiccia.description',
      'Miele di Castagno': 'products.honey.castagno.description',
      'Tartufo Nero': 'products.truffle.nero.description'
    };

    const translationKey = productMap[productName];
    if (translationKey) {
      // Use a safer approach to access nested translations
      try {
        const keys = translationKey.split('.');
        let translation: any = t;
        for (const key of keys) {
          translation = translation?.[key];
          if (!translation) break;
        }
        return translation || '';
      } catch (error) {
        console.warn(`Translation not found for key: ${translationKey}`);
        return '';
      }
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t.common.loading || 'Loading...'}</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-red-600">{error}</p>
              <button 
                onClick={() => fetchProducts()}
                className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                {t.common.retry || 'Retry'}
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t.products.title || 'Typical Abruzzo Products'}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.products.subtitle || 'Discover the authentic flavors of Abruzzo with our selection of traditional products'}
          </p>
        </div>

                 {/* Category Filter */}
         {categories.length > 1 && (
           <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
             <button
               onClick={() => handleCategoryFilter(null)}
               className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                 selectedCategory === null
                   ? 'bg-blue-600 text-white hover:bg-blue-700'
                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
               }`}
             >
               Tutti i prodotti
             </button>
             {categories.map((category) => (
               <button
                 key={category.id}
                 onClick={() => handleCategoryFilter(category.slug)}
                 className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                   selectedCategory === category.slug
                     ? 'bg-blue-600 text-white hover:bg-blue-700'
                     : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                 }`}
               >
                 {category.name}
               </button>
             ))}
           </div>
         )}

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-4xl font-semibold italic">
              {t.products.comingSoon || 'Coming soon'}
            </p>
          </div>
        ) : products.every(product => product.stock === 0) ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-4xl font-semibold">
              {t.products.comingSoon || 'Coming soon'}
            </p>
          </div>
        ) : (
          <>
            <div className={`${
              products.length <= 2 
                ? 'flex justify-center items-start gap-6' 
                : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            }`}>
              {products.map((product) => (
                <div key={product.id} className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
                  products.length <= 2 ? 'max-w-sm' : ''
                }`}>
                  {/* Product Image */}
                  <div className="aspect-w-1 aspect-h-1 w-full">
                    {getProductPreviewImage(product) ? (
                      <img
                        src={getProductPreviewImage(product)}
                        alt={product.name}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          // Fallback to original image if preview image fails to load
                          const target = e.target as HTMLImageElement;
                          // The original imageUrl is removed, so this fallback is no longer applicable
                          // Keeping it for now as it might be used elsewhere or for debugging
                          // if (product.imageUrl && target.src !== product.imageUrl) {
                          //   target.src = product.imageUrl;
                          // }
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">{t.products.noImage || 'No image'}</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-6">
                    {/* Category Badge */}
                    {product.category && (
                      <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mb-2">
                        {product.category.name}
                      </span>
                    )}

                    {/* Product Name */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {product.name}
                    </h3>

                    {/* Description */}
                    {(() => {
                      const description = getProductDescription(product.name, product.category?.slug);
                      return description ? (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {description}
                        </p>
                      ) : null;
                    })()}

                    {/* Price and Stock */}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-2xl font-bold text-green-600">
                        €{product.price.toFixed(2)}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        product.stock > 10 
                          ? 'bg-green-100 text-green-800' 
                          : product.stock > 0 
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {product.stock > 0 
                          ? `${t.products.inStock || 'In stock'}: ${product.stock}`
                          : t.products.outOfStock || 'Out of stock'
                        }
                      </span>
                    </div>

                    {/* Details Button */}
                    <Link
                      to={`/products/${product.category?.slug || 'wine'}/${product.slug || product.id}`}
                      className="w-full px-4 py-2 rounded-md text-sm font-medium transition-colors bg-green-600 text-white hover:bg-green-700 text-center block"
                    >
                      {t.common.viewDetails || 'Dettagli'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Coming Soon Message */}
            <div className="text-center mt-12 py-8">
              <p className="text-gray-600 text-lg italic">
                {t.landing?.products?.moreComingSoon || 'Altri prodotti arriveranno presto'}
              </p>
            </div>
          </>
        )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductsPage;
